import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

/** Galerie d'événements en profondeur, pilotée par le défilement. */
export default class EventDepthGallery {
  constructor(element) {
    this.element = element;
    this.events = [...element.querySelectorAll(':scope > .event')];
    this.options = { scrub: 0.7, scrollPerEvent: 2 };
    this.destroyed = false;
    gsap.registerPlugin(ScrollTrigger);
    document.fonts.ready.then(() => {
      if (!this.destroyed) this.init();
    });
  }

  init() {
    if (this.events.length < 2) return;
    this.media = gsap.matchMedia();
    this.media.add(
      '(min-width: 1024px) and (min-height: 700px) and (prefers-reduced-motion: no-preference)',
      () => {
        this.element.classList.add('events--depth');
        const originalAttributes = this.events.map((event) => ({
          hidden: event.getAttribute('aria-hidden'),
          inert: event.hasAttribute('inert'),
        }));
        this.activeIndex = -1;

        this.initAnim();
        this.setActive(0);
        // Le pin modifie la hauteur du document : recalcule aussi les sections suivantes.
        this.refreshFrame = requestAnimationFrame(() =>
          ScrollTrigger.refresh(),
        );

        return () => {
          cancelAnimationFrame(this.refreshFrame);
          this.element.classList.remove('events--depth');

          this.events.forEach((event, index) => {
            const original = originalAttributes[index];
            if (original.hidden === null) event.removeAttribute('aria-hidden');
            else event.setAttribute('aria-hidden', original.hidden);
            event.toggleAttribute('inert', original.inert);
          });
        };
      },
    );
  }

  initAnim() {
    const step = 2;
    const cards = this.events;
    // L'entrée commence avant le pin, pour ne pas traverser une scène encore vide.
    this.pin = ScrollTrigger.create({
      trigger: this.element,
      start: 'top top+=48',
      end: () =>
        `+=${window.innerHeight * cards.length * this.options.scrollPerEvent}`,
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
    });

    this.timeline = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: this.element,
        start: 'top 75%',
        end: () => this.pin.end,
        scrub: this.options.scrub,
        invalidateOnRefresh: true,
      },
      onUpdate: () => {
        const index = Math.min(
          cards.length - 1,
          Math.floor(this.timeline.time() / step),
        );
        this.setActive(index);
      },
    });

    cards.forEach((event, index) => {
      const direction = index % 2 === 0 ? -1 : 1;
      const content = event.querySelector('.event__content');
      const start = index * step;
      gsap.set(event, {
        xPercent: direction * 18,
        yPercent: 8,
        z: -200,
        scale: 0.95,
        rotationY: direction * 4,
        rotationZ: direction,
        autoAlpha: 0,
        zIndex: index + 1,
        pointerEvents: 'none',
      });
      if (content) gsap.set(content, { y: 24, autoAlpha: 0 });

      // Passe par la mise en page au repos avant de poursuivre, sans palier.
      this.timeline.to(
        event,
        {
          xPercent: 0,
          yPercent: 0,
          z: 0,
          scale: 1,
          rotationY: 0,
          rotationZ: 0,
          duration: 1.8,
        },
        start,
      );
      this.timeline.to(
        event,
        {
          xPercent: direction * -12,
          yPercent: -3,
          z: 220,
          scale: 1.1,
          rotationY: direction * -3,
          rotationZ: -direction,
          duration: 1.2,
        },
        start + 1.8,
      );
      this.timeline.to(event, { autoAlpha: 1, duration: 0.65 }, start);
      this.timeline.to(event, { autoAlpha: 0, duration: 0.8 }, start + 2.2);

      if (content) {
        this.timeline.to(content, { y: 0, duration: 1.45 }, start + 0.35);
        this.timeline.to(content, { y: -12, duration: 1.2 }, start + 1.8);
        this.timeline.to(
          content,
          { autoAlpha: 1, duration: 0.45 },
          start + 0.35,
        );
        this.timeline.to(
          content,
          { autoAlpha: 0, duration: 0.45 },
          start + 2.2,
        );
      }
    });
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.events.forEach((event, eventIndex) => {
      const active = eventIndex === index;
      event.setAttribute('aria-hidden', String(!active));
      event.toggleAttribute('inert', !active);
      gsap.set(event, { pointerEvents: active ? 'auto' : 'none' });
    });
  }

  destroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.refreshFrame);
    this.media?.revert();
  }
}
